/**
 * Team Performance — the sales manager's synopsis. Period defaults to the
 * current month. Click a rep (or a deal) for the details.
 */
import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, StatTile, Meter, Badge, Spinner, ErrorState, EmptyState, Modal, Field } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { BarChart } from '../../components/charts/index.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { pushToast } from '../ui/uiSlice.js';
import { money, moneyShort, date, number } from '../../utils/formatters.js';
import * as api from '../../api/teamPerformance.api.js';
import { StatusBadge, readPeriod, changePct, changePts } from './performanceShared.jsx';

const PERIODS = [
  { key: 'month', label: 'This month' }, { key: 'quarter', label: 'This quarter' },
  { key: 'year', label: 'This year' }, { key: 'custom', label: 'Custom range' },
];

export default function TeamPerformancePage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const { can } = useAuth();
  const { period, from, to } = readPeriod(sp);
  const repId = sp.get('rep') ?? '';
  const params = { period, from: period === 'custom' ? from : '', to: period === 'custom' ? to : '', rep_id: repId };
  const [targetFor, setTargetFor] = useState(null);

  const q = useQuery({
    queryKey: ['team-performance', params],
    queryFn: () => api.getTeamPerformance(params),
    enabled: period !== 'custom' || Boolean(from && to),
  });

  const setParam = (patch) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSp(next, { replace: true });
  };
  const keep = () => {
    const x = new URLSearchParams();
    if (period !== 'month') x.set('period', period);
    if (period === 'custom') { x.set('from', from); x.set('to', to); }
    const s = x.toString();
    return s ? `?${s}` : '';
  };
  const exportAs = (fmt) => api.exportTeamPerformance(params, fmt).catch((e) => dispatch(pushToast({ tone: 'danger', message: 'Export failed', detail: e.message })));

  const d = q.data;
  const h = d?.headline;

  const columns = [
    { key: 'name', header: 'Rep', render: (r) => (<div><div className="fw-600">{r.name}</div>{r.territory && <div className="t-xs text-muted">{r.territory}</div>}</div>) },
    {
      key: 'target', header: 'Target', align: 'end',
      render: (r) => (
        <span>
          {r.target ? money(r.target) : '—'}
          {can('targets', 'edit') && (
            <button type="button" className="btn btn-link btn-sm p-0 ms-1" title="Set monthly target" onClick={(e) => { e.stopPropagation(); setTargetFor(r); }}>
              <i className="bi bi-pencil" aria-hidden="true" />
            </button>
          )}
        </span>
      ),
    },
    { key: 'achieved', header: 'Achieved', align: 'end', render: (r) => money(r.achieved) },
    { key: 'attainmentPct', header: 'Attainment', width: '150px', render: (r) => (r.target ? (<div><div className="t-xs mb-1">{r.attainmentPct}%</div><Meter value={Math.min(100, r.attainmentPct)} tone={r.status === 'behind' ? 'rose' : r.status === 'at_risk' ? 'amber' : 'emerald'} /></div>) : '—') },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'pipeline', header: 'Pipeline', align: 'end', render: (r) => (<div>{moneyShort(r.pipeline)}<div className="t-xs text-muted">{r.openDeals} open</div></div>) },
    { key: 'winRatePct', header: 'Win rate', align: 'end', render: (r) => (r.won + r.lost ? `${r.winRatePct}%` : '—') },
    { key: 'leads', header: 'Leads', align: 'end', render: (r) => `${r.leads} → ${r.converted}` },
    { key: 'activity', header: 'Calls · Visits · Mtgs', render: (r) => `${r.calls} · ${r.visits} · ${r.meetings}` },
    { key: 'overdue', header: 'Overdue', align: 'end', render: (r) => (r.overdue ? <Badge tone="rose">{r.overdue}</Badge> : '0') },
  ];

  return (
    <>
      <PageHeader title="Team Performance" subtitle={d ? `${d.period.label} · ${d.period.from} to ${d.period.to}` : undefined}>
        {can('dashboard.manager', 'export') && (
          <>
            <button type="button" className="btn btn-edit btn-sm-x" onClick={() => exportAs('csv')}><i className="bi bi-filetype-csv me-1" aria-hidden="true" />CSV</button>
            <button type="button" className="btn btn-edit btn-sm-x" onClick={() => exportAs('pdf')}><i className="bi bi-file-earmark-pdf me-1" aria-hidden="true" />PDF</button>
          </>
        )}
      </PageHeader>

      <Card className="mb-3" bodyClass="d-flex flex-wrap gap-2 align-items-end">
        <div>
          <div className="field-x-label mb-1">Period</div>
          <select className="form-select form-select-sm" value={period} onChange={(e) => setParam({ period: e.target.value === 'month' ? '' : e.target.value, from: '', to: '' })}>
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        {period === 'custom' && (
          <>
            <div><div className="field-x-label mb-1">From</div><input type="date" className="form-control form-control-sm" value={from} onChange={(e) => setParam({ from: e.target.value })} /></div>
            <div><div className="field-x-label mb-1">To</div><input type="date" className="form-control form-control-sm" value={to} onChange={(e) => setParam({ to: e.target.value })} /></div>
          </>
        )}
        <div>
          <div className="field-x-label mb-1">Rep</div>
          <select className="form-select form-select-sm" value={repId} onChange={(e) => setParam({ rep: e.target.value })}>
            <option value="">All reps</option>
            {(d?.reps ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
      </Card>

      {q.isLoading && <Spinner />}
      {q.isError && <ErrorState error={q.error} onRetry={q.refetch} />}
      {d && !h && <EmptyState icon="bi-people" title="No team members yet" message="No one reports to you yet, so there is nothing to show." />}

      {h && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-6 col-xl"><StatTile label="Achieved" value={moneyShort(h.achieved)} icon="bi-trophy-fill" tone="emerald" delta={changePct(h.achieved, h.prev.achieved)} meta={h.target ? `${h.attainmentPct}% of ${moneyShort(h.target)} target` : 'No target set'} /></div>
            <div className="col-6 col-xl"><StatTile label="Pipeline" value={moneyShort(h.pipeline)} icon="bi-funnel-fill" tone="brand" meta={`${h.openDeals} open · forecast ${moneyShort(h.weighted)}`} /></div>
            <div className="col-6 col-xl"><StatTile label="Win rate" value={h.winRatePct} suffix="%" icon="bi-bullseye" tone="sky" delta={changePts(h.winRatePct, h.prev.winRatePct)} deltaSuffix=" pts" meta={`${h.won} won · ${h.lost} lost`} /></div>
            <div className="col-6 col-xl"><StatTile label="New leads" value={number(h.leads)} icon="bi-magnet-fill" tone="violet" delta={changePct(h.leads, h.prev.leads)} meta={`${h.converted} converted`} /></div>
            <div className="col-6 col-xl"><StatTile label="Lead conversion" value={h.conversionPct} suffix="%" icon="bi-arrow-repeat" tone="amber" delta={changePts(h.conversionPct, h.prev.conversionPct)} deltaSuffix=" pts" /></div>
          </div>

          <Card title="Rep leaderboard" subtitle="Click a rep for the full detail" bare className="mb-3">
            <DataTable columns={columns} rows={d.leaderboard} onRowClick={(r) => navigate(`/team-performance/${r.id}${keep()}`)} emptyTitle="No reps" />
          </Card>

          <div className="row g-3 mb-3">
            <div className="col-lg-7">
              <Card title="Activity by rep" subtitle={`Completed in the period · team average ${d.teamAverages.calls} calls, ${d.teamAverages.visits} visits, ${d.teamAverages.meetings} meetings`}>
                <BarChart data={d.leaderboard} xKey="name" height={240}
                  series={[{ key: 'calls', label: 'Calls' }, { key: 'visits', label: 'Visits' }, { key: 'meetings', label: 'Meetings' }, { key: 'emails', label: 'Emails' }]} />
              </Card>
            </div>
            <div className="col-lg-5">
              <Card title="Funnel" subtitle="Leads created in the period">
                <BarChart data={d.health.funnel} xKey="label" series={[{ key: 'value', label: 'Count' }]} horizontal height={240} />
              </Card>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-6">
              <Card title="Needs attention" subtitle="Coaching signals" bodyClass="d-flex flex-column gap-2 t-sm">
                {d.coaching.length === 0 && <div className="text-muted">Nothing flagged — the team looks healthy.</div>}
                {d.coaching.map((c, i) => (
                  <div className="d-flex gap-2" key={`${c.rep_id}-${c.type}-${i}`}>
                    <Badge tone={c.severity === 'high' ? 'rose' : 'amber'}>{c.severity}</Badge>
                    <div><Link to={`/team-performance/${c.rep_id}${keep()}`} className="fw-600">{c.name}</Link> — {c.message}</div>
                  </div>
                ))}
              </Card>
            </div>
            <div className="col-lg-6">
              <Card title="Waiting for your approval" actions={d.approvals.count ? <Badge tone="rose">{d.approvals.count}</Badge> : null} bodyClass="d-flex flex-column gap-2 t-sm">
                {d.approvals.items.length === 0 && <div className="text-muted">No quotations are waiting on you.</div>}
                {d.approvals.items.map((a) => (
                  <div key={a.id} className="d-flex justify-content-between">
                    <span><Link to="/quotations" className="fw-600">{a.quote_no ?? 'Quotation'}</Link> · {a.account ?? '—'} · by {a.requester ?? '—'}</span>
                    <span>{money(a.amount)} · {a.waitedHours}h</span>
                  </div>
                ))}
              </Card>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-6">
              <Card title="Stuck deals" subtitle="No stage movement for 21+ days" bare>
                <DealTable rows={d.health.stuckDeals} extra={{ key: 'days', header: 'Days idle', render: (r) => `${r.days}d` }} />
              </Card>
            </div>
            <div className="col-lg-6">
              <Card title="Closing this month" subtitle={`${d.health.closingThisMonth.count} deals · ${money(d.health.closingThisMonth.value)}`} bare>
                <DealTable rows={d.health.closingThisMonth.items} extra={{ key: 'close_date', header: 'Closes', render: (r) => date(r.close_date) }} />
              </Card>
            </div>
          </div>
        </>
      )}

      <SetTargetModal rep={targetFor} defaultMonth={(d?.period.from ?? new Date().toISOString().slice(0, 10)).slice(0, 7)}
        onClose={() => setTargetFor(null)} onSaved={() => { setTargetFor(null); qc.invalidateQueries({ queryKey: ['team-performance'] }); }} />
    </>
  );
}

function DealTable({ rows, extra }) {
  if (!rows.length) return <div className="p-3 t-sm text-muted">None.</div>;
  return (
    <div className="table-x-wrap">
      <table className="table-x is-dense">
        <thead><tr><th>Deal</th><th>Owner</th><th>Stage</th><th className="text-end">Amount</th><th>{extra.header}</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td><div className="fw-600">{r.name}</div><div className="t-xs text-muted">{r.account ?? '—'}</div></td>
              <td>{r.owner}</td><td>{r.stage}</td><td className="text-end">{money(r.amount)}</td><td>{extra.render(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SetTargetModal({ rep, defaultMonth, onClose, onSaved }) {
  const dispatch = useDispatch();
  const [month, setMonth] = useState(defaultMonth);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [seen, setSeen] = useState(null);
  if (rep && seen !== rep.id) { setSeen(rep.id); setMonth(defaultMonth); setAmount(rep.target ? String(rep.target) : ''); setError(''); }

  const save = useMutation({
    mutationFn: api.setMonthlyTarget,
    onSuccess: () => { dispatch(pushToast({ tone: 'success', message: 'Target saved' })); onSaved(); },
    onError: (e) => setError(e.message),
  });
  if (!rep) return null;
  const submit = (e) => {
    e.preventDefault();
    const v = Number(amount);
    if (!month) { setError('Choose a month.'); return; }
    if (!amount || Number.isNaN(v) || v < 0) { setError('Enter a target amount (0 or more).'); return; }
    save.mutate({ user_id: rep.id, month, target_value: v });
  };
  return (
    <Modal open onClose={onClose} title={`Monthly target — ${rep.name}`}
      footer={(<><button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button>
        <button type="submit" form="targetForm" className="btn btn-save btn-sm-x" disabled={save.isPending}>{save.isPending ? <><ButtonSpinner />Saving…</> : 'Save target'}</button></>)}>
      <form id="targetForm" onSubmit={submit} noValidate>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <div className="row g-3">
          <div className="col-md-6"><Field label="Month" required><input type="month" className="form-control" value={month} onChange={(e) => setMonth(e.target.value)} /></Field></div>
          <div className="col-md-6"><Field label="Revenue target" required><input type="number" min="0" step="any" className="form-control" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus /></Field></div>
        </div>
      </form>
    </Modal>
  );
}
